import React, { useEffect } from "react";
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TitleDescription from "../components/TitleDescription";
import Title from "../components/Title";
import { useLocation } from "react-router-dom";

import {
  OtpScreen,
  DiscoveryScreen,
  LinkingScreen,
  ReviewScreen,
  SuccessScreen,
} from "../components/ConsentJourneyPhones";

const PRIMARY_COLOR = "#6C4FE0";
const PRIMARY_SOFT = "#F5F3FF";
const PRIMARY_BORDER = "#E9E4FB";
const BORDER_COLOR = "#E2E8F0";
const FLOW_MAX_WIDTH = 1100;

// Phone mockup is authored at 380px wide; we scale it down to fit the gallery.
const PHONE_SCALE = 0.65;
const PHONE_NATIVE_W = 380;
const PHONE_NATIVE_H = 780;
const PHONE_DISPLAY_W = Math.round(PHONE_NATIVE_W * PHONE_SCALE);
const PHONE_DISPLAY_H = Math.round(PHONE_NATIVE_H * PHONE_SCALE);

function navigateToSection(hash: string) {
  const element = document.getElementById(hash);
  if (element) {
    const headerOffset = 45;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  }
}

const inlineCodeStyle = {
  backgroundColor: "#F1F3F4",
  color: "#1A202C",
  borderRadius: "4px",
  padding: "1px 6px",
  fontFamily:
    '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
  fontSize: "12px",
};

interface RoleChipProps {
  acronym: string;
  full: string;
  oneLine: string;
}

const RoleChip: React.FC<RoleChipProps> = ({ acronym, full, oneLine }) => (
  <Paper
    elevation={0}
    sx={{
      px: 2,
      py: 1.25,
      border: `1px solid ${BORDER_COLOR}`,
      borderRadius: 2,
      backgroundColor: "#FFFFFF",
      height: "100%",
      display: "flex",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    <Chip
      size="small"
      label={acronym}
      sx={{
        backgroundColor: PRIMARY_SOFT,
        color: PRIMARY_COLOR,
        border: `1px solid ${PRIMARY_BORDER}`,
        fontWeight: 700,
        fontSize: 11.5,
        letterSpacing: 0.4,
        height: 22,
      }}
    />
    <Box sx={{ minWidth: 0 }}>
      <Typography fontSize={13} fontWeight={700} sx={{ color: "#1A202C" }}>
        {full}
      </Typography>
      <Typography fontSize={12.5} sx={{ color: "#4A5568", lineHeight: 1.4 }}>
        {oneLine}
      </Typography>
    </Box>
  </Paper>
);

interface PrincipleItemProps {
  icon: React.ReactNode;
  text: string;
}

const PrincipleItem: React.FC<PrincipleItemProps> = ({ icon, text }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      backgroundColor: "#FFFFFF",
      border: `1px solid ${BORDER_COLOR}`,
      height: "100%",
    }}
  >
    <Box sx={{ color: PRIMARY_COLOR, display: "inline-flex" }}>{icon}</Box>
    <Typography fontSize={13} fontWeight={600} sx={{ color: "#1A202C" }}>
      {text}
    </Typography>
  </Box>
);

interface PhoneCardProps {
  stepNumber: number;
  caption: string;
  screen: React.ReactNode;
}

const PhoneCard: React.FC<PhoneCardProps> = ({
  stepNumber,
  caption,
  screen,
}) => (
  <Box
    sx={{
      width: PHONE_DISPLAY_W,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    <Box
      sx={{
        width: PHONE_DISPLAY_W,
        height: PHONE_DISPLAY_H,
        overflow: "hidden",
        borderRadius: `${36 * PHONE_SCALE}px`,
      }}
    >
      <Box
        sx={{
          width: PHONE_NATIVE_W,
          transform: `scale(${PHONE_SCALE})`,
          transformOrigin: "top left",
        }}
      >
        {screen}
      </Box>
    </Box>
    <Box
      sx={{
        mt: 1.5,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
      }}
    >
      <Avatar
        sx={{
          width: 22,
          height: 22,
          fontSize: 12,
          fontWeight: 700,
          bgcolor: PRIMARY_COLOR,
        }}
      >
        {stepNumber}
      </Avatar>
      <Typography
        fontSize={13}
        fontWeight={700}
        sx={{ color: "#1A202C" }}
      >
        {caption}
      </Typography>
    </Box>
  </Box>
);

interface ApiHintProps {
  stepNumber: number;
  label: string;
  code: string;
  hint: string;
}

const ApiHint: React.FC<ApiHintProps> = ({ stepNumber, label, code, hint }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      height: "100%",
      border: `1px solid ${BORDER_COLOR}`,
      borderLeft: `3px solid ${PRIMARY_COLOR}`,
      borderRadius: 1.5,
      backgroundColor: "#FFFFFF",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
      <Avatar
        sx={{
          width: 18,
          height: 18,
          fontSize: 11,
          fontWeight: 700,
          bgcolor: PRIMARY_COLOR,
        }}
      >
        {stepNumber}
      </Avatar>
      <Typography fontSize={12.5} fontWeight={700} sx={{ color: "#1A202C" }}>
        {label}
      </Typography>
    </Box>
    <Box component="code" sx={{ ...inlineCodeStyle, display: "inline-block", mb: 0.5 }}>
      {code}
    </Box>
    <Typography fontSize={12.5} sx={{ color: "#4A5568", lineHeight: 1.45 }}>
      {hint}
    </Typography>
  </Paper>
);

interface TipProps {
  icon: React.ReactNode;
  title: string;
  text: string;
}

const Tip: React.FC<TipProps> = ({ icon, title, text }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.75,
      height: "100%",
      border: `1px solid ${BORDER_COLOR}`,
      borderRadius: 2,
      backgroundColor: "#FFFFFF",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
      <Box sx={{ color: PRIMARY_COLOR, mr: 1, display: "inline-flex" }}>
        {icon}
      </Box>
      <Typography fontSize={13.5} fontWeight={700} sx={{ color: "#1A202C" }}>
        {title}
      </Typography>
    </Box>
    <Typography fontSize={12.5} sx={{ color: "#4A5568", lineHeight: 1.5 }}>
      {text}
    </Typography>
  </Paper>
);

const ConsentJourneyPage: React.FC = React.memo(() => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash.length === 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const hash = location.hash.slice(1);
    navigateToSection(hash);
  }, [location.hash]);

  const steps: PhoneCardProps[] = [
    { stepNumber: 1, caption: "AA OTP", screen: <OtpScreen /> },
    { stepNumber: 2, caption: "Discovery", screen: <DiscoveryScreen /> },
    { stepNumber: 3, caption: "Linking", screen: <LinkingScreen /> },
    { stepNumber: 4, caption: "Review", screen: <ReviewScreen /> },
    { stepNumber: 5, caption: "Approval", screen: <SuccessScreen /> },
  ];

  return (
    <Box sx={{ paddingLeft: 4, paddingRight: 4 }}>
      <TitleDescription
        title="Consent Journey"
        description="The five-screen Account Aggregator flow every customer completes before any data is shared."
      />

      {/* ============ Section: AA roles ============ */}
      <Box id="overview" sx={{ mt: 1 }}>
        <Title title="Who's who" paddingTop={4} />
        <Grid container spacing={1.5} sx={{ maxWidth: FLOW_MAX_WIDTH }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <RoleChip
              acronym="FIU"
              full="Financial Information User"
              oneLine="Requests the data (e.g. Finsense, Risk Gauge)."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <RoleChip
              acronym="AA"
              full="Account Aggregator"
              oneLine="RBI-licensed broker. Hosts the consent UI."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <RoleChip
              acronym="FIP"
              full="Financial Information Provider"
              oneLine="Holds the data — banks, depositories, NPS, GST."
            />
          </Grid>
        </Grid>
      </Box>

      {/* ============ Section: Principles ============ */}
      <Box id="principles" sx={{ mt: 1 }}>
        <Title title="Principles" paddingTop={4} />
        <Grid container spacing={1.5} sx={{ maxWidth: FLOW_MAX_WIDTH }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <PrincipleItem
              icon={<ShieldOutlinedIcon fontSize="small" />}
              text="Explicit consent"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <PrincipleItem
              icon={<GavelOutlinedIcon fontSize="small" />}
              text="Purpose-bound"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <PrincipleItem
              icon={<VisibilityOutlinedIcon fontSize="small" />}
              text="AA never sees data"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <PrincipleItem
              icon={<PauseCircleOutlineIcon fontSize="small" />}
              text="Pause or revoke anytime"
            />
          </Grid>
        </Grid>
      </Box>

      {/* ============ Section: Visual gallery ============ */}
      <Box id="journey" sx={{ mt: 2 }}>
        <Title title="The five-screen journey" paddingTop={5} />

        {/* Pill row — journey at a glance */}
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            mb: 2.5,
            p: 1.5,
            background:
              "linear-gradient(135deg, #F8F6FE 0%, #F1ECFB 100%)",
            border: `1px solid ${PRIMARY_BORDER}`,
            borderRadius: 2,
            maxWidth: FLOW_MAX_WIDTH,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            {steps.map((step, idx) => (
              <React.Fragment key={step.stepNumber}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    backgroundColor: "#FFFFFF",
                    border: `1px solid ${PRIMARY_BORDER}`,
                    borderRadius: 999,
                    px: 1.25,
                    py: 0.5,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 20,
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: PRIMARY_COLOR,
                      mr: 0.75,
                    }}
                  >
                    {step.stepNumber}
                  </Avatar>
                  <Typography
                    fontSize={12.5}
                    sx={{ color: "#1A202C", fontWeight: 600 }}
                  >
                    {step.caption}
                  </Typography>
                </Box>
                {idx < steps.length - 1 && (
                  <Box
                    sx={{
                      color: PRIMARY_COLOR,
                      display: { xs: "none", sm: "inline-flex" },
                      alignItems: "center",
                    }}
                  >
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Stack>
        </Paper>

        {/* Phone gallery */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            background: "#E9ECF2",
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: 2,
            maxWidth: FLOW_MAX_WIDTH,
            overflowX: "auto",
          }}
        >
          <Stack
            direction="row"
            spacing={3}
            alignItems="flex-start"
            sx={{ minWidth: "min-content" }}
          >
            {steps.map((step) => (
              <PhoneCard key={step.stepNumber} {...step} />
            ))}
          </Stack>
        </Paper>

        <Typography
          fontSize={12.5}
          sx={{
            mt: 1.5,
            color: "#6B7280",
            fontStyle: "italic",
            maxWidth: FLOW_MAX_WIDTH,
          }}
        >
          Representative journey — exact screens and copy are customisable per
          AA requirements.
        </Typography>
      </Box>

      {/* ============ Section: API hint per step ============ */}
      <Box id="behind-the-scenes" sx={{ mt: 4, maxWidth: FLOW_MAX_WIDTH }}>
        <Title title="What fires under the hood" paddingTop={2} />
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <ApiHint
              stepNumber={1}
              label="AA OTP"
              code="consentHandle"
              hint="AA authenticates on its own UI."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <ApiHint
              stepNumber={2}
              label="Discovery"
              code="/Accounts/discover"
              hint="FIP returns accounts matching FI types."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <ApiHint
              stepNumber={3}
              label="Linking"
              code="linkRefNumber"
              hint="FIP OTP binds accounts to the AA."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <ApiHint
              stepNumber={4}
              label="Review"
              code="Consent artefact"
              hint="Purpose, frequency, validity, retention."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <ApiHint
              stepNumber={5}
              label="Approval"
              code="/FI/request"
              hint="Webhook fires. FIU starts data fetch."
            />
          </Grid>
        </Grid>
      </Box>

      {/* ============ Section: Best practices ============ */}
      <Box id="best-practices" sx={{ mt: 5, maxWidth: FLOW_MAX_WIDTH }}>
        <Title title="Tips" paddingTop={2} />
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Tip
              icon={<ShieldOutlinedIcon fontSize="small" />}
              title="Set expectations"
              text="Tell users they're leaving for an RBI intermediary."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Tip
              icon={<GavelOutlinedIcon fontSize="small" />}
              title="Pre-fill identifiers"
              text="Pass mobile and PAN to skip prompts."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Tip
              icon={<VisibilityOutlinedIcon fontSize="small" />}
              title="Ask only what you need"
              text="Shorter, one-time consents convert better."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Tip
              icon={<PauseCircleOutlineIcon fontSize="small" />}
              title="Plan AA fallback"
              text="AA Switch reroutes on real-time success rates."
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
});

export default ConsentJourneyPage;
